import { useState, useCallback, useRef } from 'react';
import type { AppState, TabId, TabState } from '../types';
import { track } from '../analytics';

const initialState: AppState = {
  vp: { r: 'Global', sub: ['All'], status: 'All', fy: ['All'], q: ['All'], qMode: 'created', dateFrom: '', dateTo: '' },
  sd: { r: 'Global', sub: ['All'], fy: ['All'], q: ['All'], qMode: 'created', dateFrom: '', dateTo: '' },
  od: { r: 'Global', sub: ['All'], fy: ['All'], q: ['All'], qMode: 'created', dateFrom: '', dateTo: '' },
};

export function useAppState() {
  const [state, setState] = useState<AppState>(initialState);
  const [currentTab, setCurrentTab] = useState<TabId>('vp');
  const [toast, setToast] = useState<string | null>(null);
  // Mirrors currentTab so tab.switch can report `from` without side effects in a state updater.
  const currentTabRef = useRef<TabId>('vp');

  const changeTab = useCallback((to: TabId, source: 'nav' | 'drill') => {
    const from = currentTabRef.current;
    currentTabRef.current = to;
    setCurrentTab(to);
    if (from !== to) track('tab.switch', { from, to, source });
  }, []);

  const updateTab = useCallback((tab: TabId, updates: Partial<TabState>) => {
    setState(prev => ({
      ...prev,
      [tab]: { ...prev[tab], ...updates },
    }));
  }, []);

  const switchTab = useCallback((id: TabId) => {
    changeTab(id, 'nav');
  }, [changeTab]);

  const drillRegion = useCallback((reg: string, sub: string) => {
    setState(prev => ({
      ...prev,
      sd: { ...prev.sd, r: reg, sub: sub ? [sub] : ['All'] },
    }));
    changeTab('sd', 'drill');
  }, [changeTab]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  }, []);

  return { state, currentTab, toast, updateTab, switchTab, drillRegion, showToast };
}
